// src/components/coupon/CouponSelectModal.tsx
import * as React from "react";
import useSWR from "swr";
import { Button, Input, Modal, Pagination, Space, Spin, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getCouponListApi, type CouponListItem, type CouponType, type DiscountType } from "src/api/coupon/couponApi";

export type SelectedCoupon = {
  couponId: number;
  couponName: string;
  couponCode: string;
};

type Props = {
  open: boolean;
  selectedIds: number[];
  selectedItems?: SelectedCoupon[];
  singleSelect?: boolean;
  onClose: () => void;
  onConfirm: (selectedItems: SelectedCoupon[]) => void;
};

export default function CouponSelectModal({
  open,
  selectedIds,
  selectedItems,
  singleSelect = false,
  onClose,
  onConfirm,
}: Props) {
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [size] = React.useState(10);
  const [tempSelected, setTempSelected] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (open) {
      setTempSelected([...selectedIds]);
      setSearch("");
      setPage(1);
    }
  }, [open, selectedIds]);

  const { data, isLoading } = useSWR(
    open ? ["coupon/list/modal", search, page, size] : null,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getCouponListApi({
        couponName: search.trim() || undefined,
        isActive: true,
        page: apiPage,
        size: size,
        order: "createdAt,desc",
      });
    },
    { revalidateOnFocus: false }
  );

  const list = data?.data?.content ?? [];
  const totalElements = data?.data?.page?.totalElements ?? 0;

  const handleConfirm = () => {
    const result: SelectedCoupon[] = tempSelected.map((id) => {
      const coupon = list.find((item) => item.couponId === id);
      if (coupon) {
        return {
          couponId: coupon.couponId,
          couponName: coupon.couponName,
          couponCode: coupon.couponCode,
        };
      }
      const prevSelected = selectedItems?.find((item) => item.couponId === id);
      if (prevSelected) return prevSelected;
      return { couponId: id, couponName: `쿠폰 ${id}`, couponCode: "" };
    });
    onConfirm(result);
    onClose();
  };

  const getCouponTypeLabel = (type: CouponType) => {
    const labels: Record<CouponType, string> = {
      PUBLIC: "공개",
      PRIVATE: "비공개",
      AUTO: "자동",
      EVENT: "이벤트",
      FREE_SHIPPING: "무료배송",
    };
    return labels[type] || type;
  };

  const formatDiscount = (discountType: DiscountType, discountValue: number) => {
    return discountType === "FIXED"
      ? `${discountValue.toLocaleString()}원`
      : `${discountValue}%`;
  };

  const columns: ColumnsType<CouponListItem> = [
    { title: "ID", dataIndex: "couponId", width: 60 },
    { title: "쿠폰명", dataIndex: "couponName", ellipsis: true },
    { title: "코드", dataIndex: "couponCode", width: 120 },
    {
      title: "타입",
      dataIndex: "couponType",
      width: 80,
      render: (value: CouponType) => <Tag>{getCouponTypeLabel(value)}</Tag>,
    },
    {
      title: "할인",
      width: 100,
      render: (_, record) => formatDiscount(record.discountType, record.discountValue),
    },
    {
      title: "발급가능",
      dataIndex: "canIssue",
      width: 80,
      render: (value: boolean) => (
        <Tag color={value ? "green" : "default"}>{value ? "가능" : "불가"}</Tag>
      ),
    },
  ];

  const handleRowSelect = (record: CouponListItem) => {
    if (singleSelect) {
      setTempSelected([record.couponId]);
    } else {
      setTempSelected((prev) =>
        prev.includes(record.couponId)
          ? prev.filter((id) => id !== record.couponId)
          : [...prev, record.couponId]
      );
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={singleSelect ? "쿠폰 선택 (1개)" : "쿠폰 선택"}
      width={900}
      centered
      footer={
        <Space>
          <Button onClick={onClose}>취소</Button>
          <Button type="primary" onClick={handleConfirm} disabled={tempSelected.length === 0}>
            선택
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          onPressEnter={() => setPage(1)}
          placeholder="쿠폰명 검색"
        />

        <Typography.Text type="secondary">
          선택된 쿠폰: {tempSelected.length}개
          {singleSelect && " (1개만 선택 가능)"}
        </Typography.Text>

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={list}
            rowKey="couponId"
            pagination={false}
            size="small"
            scroll={{ x: 1000 }}
            rowSelection={{
              type: singleSelect ? "radio" : "checkbox",
              selectedRowKeys: tempSelected,
              onChange: (selectedRowKeys) => {
                setTempSelected(selectedRowKeys.map((k) => Number(k)));
              },
            }}
            onRow={(record) => ({
              onClick: () => handleRowSelect(record),
            })}
          />
        )}

        <div style={{ display: "flex", justifyContent: "center" }}>
          <Pagination
            current={page}
            total={Math.max(1, totalElements)}
            pageSize={size}
            onChange={(nextPage) => setPage(nextPage)}
            showSizeChanger={false}
          />
        </div>
      </Space>
    </Modal>
  );
}
